import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    IconButton,
    Divider,
    Stack,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Save as SaveIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material';
import { formatSupplierForAutocomplete } from '../utils/supplierFormatter';
import { usePmHubStyles } from '../hooks/useHubStyles';
import { getHubFieldSx } from '../theme/hubThemes';
import { HubDialogCancelButton, HubDialogSubmitButton } from './common/HubDialogButtons';
import { projectAccountsApi, invoicesApi } from '../services/api';
import { projectAccountTypeLabelKey } from '../constants/projectAccountTypes';
import { formatInvoiceMoney } from '../utils/invoiceFormat';
import {
    TransactionAmountWithDirection,
    TransactionDirectionColumnHeader,
    TransactionDirectionToggleGroup,
} from './transactions/TransactionDirectionDisplay';
import {
    signedTransactionAmount,
    signedAllocationsTotal,
    allocationsBalanceHeader,
    formatDisplayAmount,
    directionFromSignedValue,
    TRANSACTION_INCOME,
    TRANSACTION_EXPENSE,
} from '../utils/transactionAmount';

function invoicePaymentProblems(allocations, invoicesByProject, headerDirection) {
    const problems = [];
    const totals = new Map();
    allocations.forEach((allocation) => {
        const direction = allocation.direction || headerDirection;
        if (direction !== TRANSACTION_INCOME || !allocation.pays_invoice) return;
        if (!allocation.invoice_id) {
            problems.push({ type: 'required' });
            return;
        }
        const invoices = invoicesByProject[allocation.project_id];
        if (!Array.isArray(invoices)) return;
        const invoice = invoices.find((row) => row.id === allocation.invoice_id);
        if (!invoice) {
            problems.push({ type: 'missing' });
            return;
        }
        const account = (invoice.accounts || []).find(
            (row) => String(row.project_account_id) === String(allocation.project_account_id),
        );
        if (!account) {
            problems.push({ type: 'account', number: invoice.number });
            return;
        }
        const key = `${invoice.id}:${account.project_account_id}`;
        const current = totals.get(key);
        totals.set(key, {
            amount: (current?.amount || 0) + (Number(allocation.amount) || 0),
            remaining: Number(account.remaining),
            number: invoice.number,
            accountName: account.project_account_name,
            currency: invoice.currency,
        });
    });
    totals.forEach((row) => {
        if (row.amount - row.remaining > 0.009) problems.push({ type: 'over', ...row });
    });
    return problems;
}

const TransactionForm = ({
    initialData = null,
    projects = [],
    activities = [],
    accounts = [],
    suppliers = [],
    onSupplierListScroll,
    suppliersLoadingMore = false,
    onActivityListScroll,
    activitiesLoadingMore = false,
    onSupplierInputChange,
    onActivityInputChange,
    onSupplierSelectedIdChange,
    onActivitySelectedIdChange,
    onSave,
    onDelete,
    onCancel,
    loading = false,
    saving = false,
    deleting = false,
    error = null,
    readOnly = false,
    submitDisabled = false,
    footerStart = null,
    submitLabel = null,
    submittingLabel = null,
}) => {
    const { t } = useTranslation();
    const styles = usePmHubStyles();
    const fieldSx = getHubFieldSx(styles);
    const formDisabled = readOnly || saving || deleting;
    const [formData, setFormData] = useState({
        account: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        supplier: '',
        direction: 'expense',
    });

    const [projectAllocations, setProjectAllocations] = useState([]);
    const [formError, setFormError] = useState(null);
    const [linkedMode, setLinkedMode] = useState(true);
    /** projectId -> array of that project's project accounts (lazy-loaded). */
    const [projectAccountsByProject, setProjectAccountsByProject] = useState({});
    const [invoicesByProject, setInvoicesByProject] = useState({});
    const [supplierInputDisplay, setSupplierInputDisplay] = useState('');
    const [activityInputByIndex, setActivityInputByIndex] = useState({});

    // Data fetching removed - component receives all data as props

    /** When editing, options load after the transaction — fill the visible input once options include the value. */
    useEffect(() => {
        if (!onSupplierInputChange || !initialData) return;
        if (!formData.supplier) {
            setSupplierInputDisplay('');
            return;
        }
        const opt = suppliers.find((s) => String(s.id) === String(formData.supplier));
        if (!opt) return;
        setSupplierInputDisplay((prev) =>
            prev === '' ? formatSupplierForAutocomplete(opt) : prev
        );
    }, [formData.supplier, suppliers, onSupplierInputChange, initialData]);

    useEffect(() => {
        if (!onActivityInputChange || !initialData) return;
        setActivityInputByIndex((prev) => {
            let changed = false;
            const next = { ...prev };
            projectAllocations.forEach((allocation, index) => {
                if ((next[index] == null || next[index] === '') && allocation.activity_name) {
                    next[index] = allocation.activity_name;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [projectAllocations, onActivityInputChange, initialData]);

    const activityOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        const add = (activity) => {
            if (activity == null || activity.id == null || activity.id === '') return;
            const key = String(activity.id);
            if (seen.has(key)) return;
            seen.add(key);
            options.push(activity);
        };
        activities.forEach(add);
        projectAllocations.forEach((allocation) => {
            if (allocation.activity) {
                add({ id: allocation.activity, name: allocation.activity_name || '' });
            }
        });
        return options;
    }, [activities, projectAllocations]);

    useEffect(() => {
        if (!initialData) return;
            setFormData({
                account:
                    typeof initialData.account === 'object' && initialData.account
                        ? initialData.account.name
                        : initialData.account || '',
                amount: initialData.amount || '',
                description: initialData.description || '',
                date: initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                supplier:
                    typeof initialData.supplier === 'object' && initialData.supplier
                        ? initialData.supplier.id
                        : initialData.supplier || '',
                direction: initialData.direction || 'expense',
            });

            // Load project allocations if they exist. Activity, materials, and the
            // project account now live on each allocation (project transaction).
            if (initialData.project_transactions) {
                setProjectAllocations(initialData.project_transactions.map(pt => ({
                    project_id:
                        typeof pt.project === 'object' && pt.project ? pt.project.id : pt.project,
                    project_name:
                        pt.project?.location ||
                        pt.project_location ||
                        (typeof pt.project === 'string' ? pt.project : ''),
                    amount: pt.amount,
                    project_account_id: pt.project_account ?? null,
                    activity:
                        (typeof pt.activity === 'object' && pt.activity ? pt.activity.id : pt.activity)
                        || '',
                    activity_name: pt.activity?.name || pt.activity_name || '',
                    is_materials: pt.is_materials ?? false,
                    direction: pt.direction || initialData.direction || 'expense',
                    invoice_id: pt.invoice ?? null,
                    invoice_number: pt.invoice_number || '',
                    pays_invoice: Boolean(pt.invoice),
                })));
            }
    }, [initialData]);

    // Lazy-load project accounts for the projects referenced by the allocations.
    useEffect(() => {
        const projectIds = Array.from(new Set(
            projectAllocations
                .map((pa) => pa.project_id)
                .filter((id) => id != null && id !== ''),
        ));
        projectIds.forEach((pid) => {
            if (projectAccountsByProject[pid] !== undefined) return;
            // Mark as loading to avoid duplicate fetches.
            setProjectAccountsByProject((prev) => ({ ...prev, [pid]: prev[pid] ?? null }));
            projectAccountsApi.getByProject(pid)
                .then((data) => {
                    setProjectAccountsByProject((prev) => ({
                        ...prev,
                        [pid]: Array.isArray(data) ? data : [],
                    }));
                })
                .catch((err) => {
                    console.error('Failed to load project accounts:', err);
                    setProjectAccountsByProject((prev) => ({ ...prev, [pid]: [] }));
                });
        });
    }, [projectAllocations, projectAccountsByProject]);

    const transactionId = initialData?.id;
    useEffect(() => {
        const projectIds = Array.from(new Set(
            projectAllocations
                .filter((pa) => (pa.direction || 'expense') === TRANSACTION_INCOME)
                .map((pa) => pa.project_id)
                .filter((id) => id != null && id !== ''),
        ));
        projectIds.forEach((pid) => {
            if (invoicesByProject[pid] !== undefined) return;
            setInvoicesByProject((prev) => ({ ...prev, [pid]: prev[pid] ?? null }));
            invoicesApi.list({
                project_id: pid,
                payable: '1',
                page_size: 100,
                ...(transactionId ? { exclude_transaction: transactionId } : {}),
            })
                .then((data) => {
                    const rows = Array.isArray(data?.results) ? data.results : [];
                    setInvoicesByProject((prev) => ({ ...prev, [pid]: rows }));
                })
                .catch((err) => {
                    console.error('Failed to load invoices:', err);
                    setInvoicesByProject((prev) => ({ ...prev, [pid]: [] }));
                });
        });
    }, [projectAllocations, invoicesByProject, transactionId]);

    // Auto-select when exactly one expenses account exists (legacy data without stored id).
    useEffect(() => {
        setProjectAllocations((prev) => {
            let changed = false;
            const next = prev.map((pa) => {
                if (!pa.project_id || pa.project_account_id) return pa;
                const accs = projectAccountsByProject[pa.project_id];
                if (!Array.isArray(accs) || accs.length !== 1) return pa;
                changed = true;
                return { ...pa, project_account_id: accs[0].id };
            });
            return changed ? next : prev;
        });
    }, [projectAccountsByProject]);

    // Update the single project allocation when the amount is changed with proper allocation
    useEffect(() => {
        if (!linkedMode || !formData.amount || projectAllocations.length !== 1) return;
        setProjectAllocations((prev) => {
            const next = {
                ...prev[0],
                amount: parseFloat(formData.amount),
                direction: formData.direction || TRANSACTION_EXPENSE,
            };
            if (prev[0]?.amount === next.amount && prev[0]?.direction === next.direction) {
                return prev;
            }
            return [next];
        });
    }, [formData.amount, formData.direction, linkedMode, projectAllocations.length]);

    const handleInputChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const headerDirection = formData.direction || TRANSACTION_EXPENSE;

    const getSignedTotalAllocated = () => signedAllocationsTotal(projectAllocations, headerDirection);

    const getAllocationBalance = () => {
        const headerSigned = signedTransactionAmount(parseFloat(formData.amount || 0), headerDirection);
        return headerSigned - getSignedTotalAllocated();
    };

    const allocationsAreBalanced = () => allocationsBalanceHeader(
        parseFloat(formData.amount || 0),
        headerDirection,
        projectAllocations,
    );

    const handleAddProject = () => {
        if (projectAllocations.length === 0) return;

        const balance = getAllocationBalance();

        if (Math.abs(balance) < 0.01) {
            setFormError(t('app.addTransaction.form.validation.fullyAllocated'));
            return;
        }

        const newAllocation = {
            project_id: null,
            project_name: '',
            amount: Math.abs(balance),
            direction: balance < 0 ? TRANSACTION_INCOME : TRANSACTION_EXPENSE,
        };

        setProjectAllocations((prev) => [...prev, newAllocation]);
        setFormError(null);
    };

    const handleRemoveProject = (index) => {
        if (projectAllocations.length <= 1) {
            setFormError(t('app.addTransaction.form.validation.minOneAllocation'));
            return;
        }
        setProjectAllocations((prev) => prev.filter((_, i) => i !== index));
        setFormError(null);
    };

    const handleProjectChange = (index, projectId) => {
        if (projectId == null || projectId === '') {
            setProjectAllocations((prev) =>
                prev.map((pa, i) =>
                    i === index ? { ...pa, project_id: null, project_name: '', project_account_id: null, pays_invoice: false, invoice_id: null, invoice_number: '' } : pa
                )
            );
            return;
        }
        const project = projects.find((p) => p.id === projectId);
        if (!project) return;

        setProjectAllocations((prev) =>
            prev.map((pa, i) =>
                i === index
                    ? { ...pa, project_id: projectId, project_name: project.location, project_account_id: null, pays_invoice: false, invoice_id: null, invoice_number: '' }
                    : pa
            )
        );
    };

    const handleProjectAccountChange = (index, projectAccountId) => {
        setProjectAllocations((prev) =>
            prev.map((pa, i) =>
                i === index ? { ...pa, project_account_id: projectAccountId || null } : pa
            )
        );
    };

    const handleAllocationActivityChange = (index, activity) => {
        onActivitySelectedIdChange?.(activity ? activity.id : null);
        setActivityInputByIndex((prev) => ({
            ...prev,
            [index]: activity ? (activity.name || '') : '',
        }));
        setProjectAllocations((prev) =>
            prev.map((pa, i) =>
                i === index
                    ? {
                        ...pa,
                        activity: activity ? activity.id : '',
                        activity_name: activity ? activity.name : '',
                    }
                    : pa
            )
        );
    };

    const handleAllocationMaterialsChange = (index, isMaterials) => {
        setProjectAllocations((prev) =>
            prev.map((pa, i) => (i === index ? { ...pa, is_materials: isMaterials } : pa))
        );
    };

    const handleAllocationDirectionChange = (index, direction) => {
        setProjectAllocations((prev) =>
            prev.map((pa, i) => (
                i === index
                    ? {
                        ...pa,
                        direction,
                        ...(direction === TRANSACTION_INCOME
                            ? {}
                            : { pays_invoice: false, invoice_id: null, invoice_number: '' }),
                    }
                    : pa
            ))
        );
        setLinkedMode(false);
    };

    const handlePaysInvoiceChange = (index, paysInvoice) => {
        setProjectAllocations((prev) =>
            prev.map((pa, i) => (
                i === index
                    ? {
                        ...pa,
                        pays_invoice: paysInvoice,
                        ...(paysInvoice ? {} : { invoice_id: null, invoice_number: '' }),
                    }
                    : pa
            ))
        );
    };

    const handleInvoiceChange = (index, invoiceId) => {
        setProjectAllocations((prev) => prev.map((pa, i) => {
            if (i !== index) return pa;
            const invoices = invoicesByProject[pa.project_id];
            const invoice = Array.isArray(invoices)
                ? invoices.find((row) => String(row.id) === String(invoiceId))
                : null;
            if (!invoice) {
                return { ...pa, invoice_id: null, invoice_number: '' };
            }
            const payableAccounts = (invoice.accounts || []).filter((row) => Number(row.remaining) > 0);
            const currentMatches = payableAccounts.some(
                (row) => String(row.project_account_id) === String(pa.project_account_id),
            );
            return {
                ...pa,
                invoice_id: invoice.id,
                invoice_number: invoice.number || '',
                project_account_id: currentMatches
                    ? pa.project_account_id
                    : (payableAccounts[0]?.project_account_id ?? pa.project_account_id),
            };
        }));
    };

    const handleAmountChange = (index, amount) => {
        setProjectAllocations(prev => prev.map((pa, i) =>
            i === index ? { ...pa, amount: parseFloat(amount) || 0 } : pa
        ));
        const nextAllocations = projectAllocations.map((pa, i) =>
            i === index ? { ...pa, amount: parseFloat(amount) || 0 } : pa
        );
        if (nextAllocations.length === 1 && allocationsBalanceHeader(
            parseFloat(formData.amount || 0),
            headerDirection,
            nextAllocations,
        )) {
            setLinkedMode(true);
        } else {
            setLinkedMode(false);
        }
    };

    const formatSignedCurrency = (magnitude) => `$${(Math.abs(Number(magnitude) || 0)).toFixed(2)}`;

    const formatSignedNet = (signedValue) => formatDisplayAmount(
        Math.abs(signedValue),
        directionFromSignedValue(signedValue),
        formatSignedCurrency,
    );

    const fieldLabel = (key) => t(`app.addTransaction.form.${key}`);

    const getMissingRequiredFields = () => {
        const missing = [];
        if (!formData.account) missing.push(fieldLabel('account'));
        if (formData.amount === '' || formData.amount == null) missing.push(fieldLabel('amount'));
        // Supplier is optional now (nullable): non-supplier flows have none.
        return missing;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (readOnly) return;

        const missingFields = getMissingRequiredFields();
        if (missingFields.length > 0) {
            setFormError(t('app.addTransaction.form.validation.missingFields', {
                fields: missingFields.join(', '),
            }));
            return;
        }

        if (projectAllocations.length === 0) {
            setFormError(t('app.addTransaction.form.validation.projectAllocationRequired'));
            return;
        }

        if (projectAllocations.some((pa) => pa.project_id == null || pa.project_id === '')) {
            setFormError(t('app.addTransaction.form.validation.projectRowRequired'));
            return;
        }

        const allocationKeys = projectAllocations.map((pa) => {
            const accountId = pa.project_account_id ?? '';
            const activityKey = pa.activity || '';
            const invoiceKey = pa.pays_invoice && pa.invoice_id ? pa.invoice_id : '';
            return `${accountId}|${activityKey}|${invoiceKey}`;
        });
        if (allocationKeys.length !== new Set(allocationKeys).size) {
            setFormError(t('app.addTransaction.form.validation.duplicateAllocation'));
            return;
        }

        const paymentProblems = invoicePaymentProblems(
            projectAllocations,
            invoicesByProject,
            headerDirection,
        );
        if (paymentProblems.some((problem) => problem.type === 'required' || problem.type === 'missing')) {
            setFormError(t('app.addTransaction.form.validation.invoiceRequired'));
            return;
        }
        const accountProblem = paymentProblems.find((problem) => problem.type === 'account');
        if (accountProblem) {
            setFormError(t('app.addTransaction.form.validation.invoiceAccount', {
                number: accountProblem.number,
            }));
            return;
        }
        const overProblem = paymentProblems.find((problem) => problem.type === 'over');
        if (overProblem) {
            setFormError(t('app.addTransaction.form.validation.invoiceOverpay', {
                number: overProblem.number,
                remaining: formatInvoiceMoney(overProblem.remaining, overProblem.currency),
                account: overProblem.accountName,
            }));
            return;
        }

        const totalAmount = parseFloat(formData.amount);

        if (!allocationsAreBalanced()) {
            setFormError(t('app.addTransaction.form.validation.totalMismatch', {
                allocated: formatSignedNet(getSignedTotalAllocated()),
                amount: formatDisplayAmount(totalAmount, headerDirection, formatSignedCurrency),
            }));
            return;
        }

        const transactionData = {
            account: formData.account,
            amount: totalAmount,
            description: formData.description,
            date: formData.date,
            supplier: formData.supplier,
            direction: headerDirection,
            project_allocations: projectAllocations.map((pa) => {
                const direction = pa.direction || headerDirection;
                const paysInvoice = direction === TRANSACTION_INCOME && pa.pays_invoice && pa.invoice_id;
                return {
                    ...pa,
                    activity: pa.activity || null,
                    direction,
                    invoice: paysInvoice ? pa.invoice_id : null,
                };
            }),
        };

        onSave(transactionData);
    };

    const handleDelete = () => {
        if (readOnly || !onDelete) return;
        if (window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
            onDelete();
        }
    };

    // Loading state handled by parent component

    return (
        <Box>
            {(error || formError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || formError}
                </Alert>
            )}
            {readOnly && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('app.editTransaction.reconciledReadOnly')}
                </Alert>
            )}
            <Box
                component="fieldset"
                disabled={readOnly}
                sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
            >
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label={t('app.addTransaction.form.date')}
                            type="date"
                            value={formData.date}
                            onChange={handleInputChange('date')}
                            InputLabelProps={{ shrink: true }}
                            disabled={formDisabled}
                            sx={fieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            fullWidth
                            options={accounts}
                            getOptionLabel={(account) => {
                                if (typeof account === 'string') return account;
                                return account.name || '';
                            }}
                            value={accounts.find(a => a.name === formData.account) || null}
                            onChange={(event, newValue) => {
                                setFormData(prev => ({
                                    ...prev,
                                    account: newValue ? newValue.name : ''
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('app.addTransaction.form.account')}
                                    required
                                    placeholder={t('app.addTransaction.form.selectAccount')}
                                    sx={fieldSx}
                                />
                            )}
                            renderOption={({ key, ...props }, account) => (
                                <Box component="li" key={key} {...props}>
                                    <Typography variant="body1">
                                        {account.name}
                                    </Typography>
                                </Box>
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            noOptionsText="No accounts found"
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            fullWidth
                            options={suppliers}
                            filterOptions={(opts) => opts}
                            getOptionLabel={(supplier) => {
                                if (typeof supplier === 'string') return supplier;
                                return formatSupplierForAutocomplete(supplier);
                            }}
                            value={
                                suppliers.find(
                                    (s) => String(s.id) === String(formData.supplier ?? '')
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                setFormData(prev => ({
                                    ...prev,
                                    supplier: newValue ? newValue.id : ''
                                }));
                                onSupplierSelectedIdChange?.(newValue ? newValue.id : null);
                                if (onSupplierInputChange) {
                                    setSupplierInputDisplay(
                                        newValue ? formatSupplierForAutocomplete(newValue) : ''
                                    );
                                }
                            }}
                            inputValue={onSupplierInputChange ? supplierInputDisplay : undefined}
                            onInputChange={(event, newInputValue, reason) => {
                                if (!onSupplierInputChange) return;
                                if (reason === 'input') {
                                    setSupplierInputDisplay(newInputValue);
                                    onSupplierInputChange(newInputValue);
                                } else if (reason === 'clear') {
                                    setSupplierInputDisplay('');
                                    onSupplierInputChange('');
                                }
                                // Do not handle "reset" here: it runs after selection and would clear
                                // this controlled inputValue, leaving the field blank while value is set.
                            }}
                            ListboxProps={
                                onSupplierListScroll
                                    ? {
                                          onScroll: onSupplierListScroll,
                                          sx: { maxHeight: 280, overflow: 'auto' },
                                      }
                                    : undefined
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('app.addTransaction.form.supplier')}
                                    placeholder={t('app.addTransaction.form.selectSupplier')}
                                    sx={fieldSx}
                                />
                            )}
                            renderOption={({ key, ...props }, supplier) => (
                                <Box component="li" key={key} {...props}>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {supplier.id} {supplier.first_name || ''} {supplier.last_name || ''}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {supplier.company || 'No company'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            isOptionEqualToValue={(option, value) =>
                                option != null &&
                                value != null &&
                                String(option.id) === String(value.id)
                            }
                            noOptionsText="No suppliers found"
                            loading={suppliersLoadingMore}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label={t('app.addTransaction.form.description')}
                            multiline
                            rows={1}
                            value={formData.description}
                            onChange={handleInputChange('description')}
                            placeholder={t('app.addTransaction.form.enterNotes')}
                            sx={fieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            required
                            label={t('app.addTransaction.form.amount')}
                            type="number"
                            value={formData.amount}
                            onChange={handleInputChange('amount')}
                            inputProps={{ step: "0.01", min: "0" }}
                            sx={fieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TransactionDirectionToggleGroup
                            fullWidth
                            value={formData.direction || 'expense'}
                            onChange={(value) => {
                                setFormData((prev) => ({ ...prev, direction: value }));
                            }}
                            disabled={formDisabled}
                        />
                    </Grid>

                    {/* Project Allocation Section */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2, borderColor: styles.divider }} />
                        <Typography variant="h6" gutterBottom sx={{ color: styles.bodyText, fontWeight: 600 }}>
                            {t('app.addTransaction.form.projectAllocations')}
                        </Typography>
                        <Typography variant="body2" paragraph sx={{ color: styles.bodyMuted }}>
                            {t('app.addTransaction.form.projectAllocationDescription')}
                        </Typography>

                        {/* Project Allocation Table */}
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                border: 1,
                                borderColor: styles.divider,
                                bgcolor: styles.assistantBubbleBg,
                                '& .MuiTableCell-head': {
                                    color: styles.bodyMuted,
                                    borderColor: styles.divider,
                                    fontWeight: 600,
                                },
                                '& .MuiTableCell-root': {
                                    color: styles.bodyText,
                                    borderColor: styles.divider,
                                },
                                '& .MuiTableBody-root .MuiTableCell-root': {
                                    py: 2,
                                },
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('app.addTransaction.form.project')}</TableCell>
                                        <TableCell align="center">
                                            <TransactionDirectionColumnHeader />
                                        </TableCell>
                                        <TableCell align="right">{t('app.addTransaction.form.projectAmount')}</TableCell>
                                        <TableCell align="center" width="100">{t('app.addTransaction.form.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {projectAllocations.map((allocation, index) => (
                                        <TableRow key={`allocation-${index}-${allocation.project_id ?? 'none'}`}>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Stack spacing={2}>
                                                <Autocomplete
                                                    fullWidth
                                                    size="small"
                                                    options={projects}
                                                    getOptionLabel={(project) => {
                                                        if (typeof project === 'string') return project;
                                                        return `${project.location} (${project.id})`;
                                                    }}
                                                    value={
                                                        allocation.project_id != null && allocation.project_id !== ''
                                                            ? projects.find((p) => p.id === allocation.project_id) || null
                                                            : null
                                                    }
                                                    onChange={(event, newValue) => {
                                                        handleProjectChange(
                                                            index,
                                                            newValue != null ? newValue.id : null
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder={t('app.addTransaction.form.selectProject')}
                                                            variant="outlined"
                                                            sx={fieldSx}
                                                        />
                                                    )}
                                                    renderOption={({ key, ...props }, project) => (
                                                        <Box component="li" key={key} {...props}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {project.location}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    ID: {project.id}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                                    noOptionsText="No available projects"
                                                />
                                                {allocation.project_id && (() => {
                                                    const accounts = projectAccountsByProject[allocation.project_id];
                                                    if (!Array.isArray(accounts)) {
                                                        return (
                                                            <Typography variant="caption" sx={{ color: styles.bodyMuted, display: 'block', mt: 0.5 }}>
                                                                {t('app.transactionTypes.loadingAccounts')}
                                                            </Typography>
                                                        );
                                                    }
                                                    if (accounts.length === 0) {
                                                        return (
                                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                                                {t('app.transactionTypes.noMatchingAccount')}
                                                            </Typography>
                                                        );
                                                    }
                                                    return (
                                                        <TextField
                                                            select
                                                            size="small"
                                                            fullWidth
                                                            label={t('app.transactionTypes.projectAccountLabel')}
                                                            value={allocation.project_account_id || ''}
                                                            onChange={(e) => handleProjectAccountChange(index, e.target.value)}
                                                            sx={fieldSx}
                                                        >
                                                            {accounts.map((pa) => (
                                                                <MenuItem key={pa.id} value={pa.id}>
                                                                    {pa.name} · {t(projectAccountTypeLabelKey(pa.account_type))}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    );
                                                })()}
                                                {(allocation.direction || headerDirection) === TRANSACTION_INCOME && (
                                                    <>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!allocation.pays_invoice}
                                                                    onChange={(e) => handlePaysInvoiceChange(index, e.target.checked)}
                                                                    sx={{
                                                                        color: styles.bodyMuted,
                                                                        '&.Mui-checked': { color: styles.border },
                                                                    }}
                                                                />
                                                            }
                                                            label={t('app.addTransaction.form.paysInvoice')}
                                                            sx={{ color: styles.bodyText, ml: 0, mr: 0 }}
                                                        />
                                                        {allocation.pays_invoice && (() => {
                                                            const projectInvoices = invoicesByProject[allocation.project_id];
                                                            if (!Array.isArray(projectInvoices)) {
                                                                return (
                                                                    <Typography variant="caption" sx={{ color: styles.bodyMuted }}>
                                                                        {t('app.addTransaction.form.loadingInvoices')}
                                                                    </Typography>
                                                                );
                                                            }
                                                            const options = projectInvoices.filter((invoice) => (
                                                                Number(invoice.remaining) > 0
                                                                || invoice.id === allocation.invoice_id
                                                            ));
                                                            const selectedInvoice = options.find(
                                                                (invoice) => invoice.id === allocation.invoice_id,
                                                            );
                                                            const selectedAccount = (selectedInvoice?.accounts || []).find(
                                                                (row) => String(row.project_account_id) === String(allocation.project_account_id),
                                                            );
                                                            return (
                                                                <TextField
                                                                    select
                                                                    size="small"
                                                                    fullWidth
                                                                    label={t('app.addTransaction.form.invoice')}
                                                                    value={allocation.invoice_id || ''}
                                                                    onChange={(e) => handleInvoiceChange(index, e.target.value)}
                                                                    helperText={selectedAccount
                                                                        ? t('app.addTransaction.form.invoicePaymentHelp', {
                                                                            remaining: formatInvoiceMoney(
                                                                                selectedAccount.remaining,
                                                                                selectedInvoice.currency,
                                                                            ),
                                                                            account: selectedAccount.project_account_name,
                                                                        })
                                                                        : t('app.addTransaction.form.invoicePaymentHint')}
                                                                    sx={fieldSx}
                                                                >
                                                                    <MenuItem value="" disabled>
                                                                        {options.length === 0
                                                                            ? t('app.addTransaction.form.noPayableInvoices')
                                                                            : t('app.addTransaction.form.invoice')}
                                                                    </MenuItem>
                                                                    {options.map((invoice) => (
                                                                        <MenuItem key={invoice.id} value={invoice.id}>
                                                                            {invoice.number}
                                                                            {' · '}
                                                                            {formatInvoiceMoney(invoice.remaining, invoice.currency)}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            );
                                                        })()}
                                                    </>
                                                )}
                                                <Autocomplete
                                                    fullWidth
                                                    size="small"
                                                    options={activityOptions}
                                                    filterOptions={(opts) => opts}
                                                    getOptionLabel={(activity) => {
                                                        if (typeof activity === 'string') return activity;
                                                        return activity.name || '';
                                                    }}
                                                    value={
                                                        allocation.activity
                                                            ? activityOptions.find(
                                                                (a) => String(a.id) === String(allocation.activity),
                                                            ) || null
                                                            : null
                                                    }
                                                    onChange={(event, newValue) => handleAllocationActivityChange(index, newValue)}
                                                    inputValue={onActivityInputChange ? (activityInputByIndex[index] ?? '') : undefined}
                                                    onInputChange={(event, newInputValue, reason) => {
                                                        if (!onActivityInputChange) return;
                                                        if (reason === 'input') {
                                                            setActivityInputByIndex((prev) => ({
                                                                ...prev,
                                                                [index]: newInputValue,
                                                            }));
                                                            onActivityInputChange(newInputValue);
                                                        } else if (reason === 'clear') {
                                                            setActivityInputByIndex((prev) => ({
                                                                ...prev,
                                                                [index]: '',
                                                            }));
                                                            onActivityInputChange('');
                                                        }
                                                        // Do not handle "reset": it runs after selection/blur and
                                                        // would clear this controlled input while a value is set.
                                                    }}
                                                    ListboxProps={
                                                        onActivityListScroll
                                                            ? { onScroll: onActivityListScroll, sx: { maxHeight: 280, overflow: 'auto' } }
                                                            : undefined
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={t('app.addTransaction.form.activity')}
                                                            placeholder={t('app.addTransaction.form.selectActivity')}
                                                            sx={fieldSx}
                                                        />
                                                    )}
                                                    renderOption={({ key, ...props }, activity) => (
                                                        <Box component="li" key={activity.id ?? key} {...props}>
                                                            {activity.name}
                                                        </Box>
                                                    )}
                                                    isOptionEqualToValue={(option, value) =>
                                                        option != null && value != null && String(option.id) === String(value.id)
                                                    }
                                                    noOptionsText="No activities found"
                                                    loading={activitiesLoadingMore}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={!!allocation.is_materials}
                                                            onChange={(e) => handleAllocationMaterialsChange(index, e.target.checked)}
                                                            sx={{
                                                                color: styles.bodyMuted,
                                                                '&.Mui-checked': { color: styles.border },
                                                            }}
                                                        />
                                                    }
                                                    label={t('app.addTransaction.form.isMaterials')}
                                                    sx={{ color: styles.bodyText, ml: 0, mr: 0, mt: 0.5 }}
                                                />
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <TransactionDirectionToggleGroup
                                                    value={allocation.direction || headerDirection}
                                                    onChange={(value) => handleAllocationDirectionChange(index, value)}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={allocation.amount}
                                                    onChange={(e) => handleAmountChange(index, e.target.value)}
                                                    inputProps={{ step: "0.01", min: "0" }}
                                                    sx={{ width: 120, ...fieldSx }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveProject(index)}
                                                    color="error"
                                                    disabled={projectAllocations.length <= 1}
                                                >
                                                    <RemoveIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Add Project Button */}
                        <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddProject}
                                disabled={projectAllocations.length === 0 || allocationsAreBalanced()}
                                sx={{
                                    borderColor: styles.border,
                                    color: styles.rowButtonColor,
                                    '&:hover': {
                                        borderColor: styles.buttonHover,
                                        bgcolor: 'transparent',
                                    },
                                }}
                            >
                                {t('app.addTransaction.form.addProject')}
                            </Button>
                        </Box>

                        {/* Summary */}
                        <Box
                            mt={2}
                            p={2}
                            borderRadius={2}
                            sx={{
                                border: 1,
                                borderColor: styles.divider,
                                bgcolor: styles.assistantBubbleBg,
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ color: styles.bodyMuted }}>
                                        {t('app.addTransaction.form.transactionAmount')}
                                    </Typography>
                                    <TransactionAmountWithDirection
                                        amount={parseFloat(formData.amount || 0)}
                                        direction={headerDirection}
                                        formatMagnitude={formatSignedCurrency}
                                        variant="h6"
                                        iconSize="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ color: styles.bodyMuted }}>
                                        {t('app.addTransaction.form.totalAllocated')}
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        <TransactionAmountWithDirection
                                            amount={Math.abs(getSignedTotalAllocated())}
                                            direction={directionFromSignedValue(getSignedTotalAllocated())}
                                            formatMagnitude={formatSignedCurrency}
                                            variant="h6"
                                            iconSize="small"
                                            sx={{
                                                color: allocationsAreBalanced() ? undefined : 'error.main',
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            display="flex"
                            gap={2}
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{
                                pt: 2.5,
                                mt: 1,
                                borderTop: 1,
                                borderColor: styles.divider,
                            }}
                        >
                            {footerStart ? (
                                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 }, maxWidth: { sm: '50%' } }}>
                                    {footerStart}
                                </Box>
                            ) : null}
                            <Box
                                display="flex"
                                gap={2}
                                flexWrap="wrap"
                                justifyContent="flex-end"
                                sx={{ ml: footerStart ? 'auto' : 0, width: footerStart ? 'auto' : '100%' }}
                            >
                            <HubDialogCancelButton
                                onClick={onCancel}
                                disabled={saving || deleting}
                            >
                                {t('app.addTransaction.form.cancel')}
                            </HubDialogCancelButton>

                            {!readOnly && onDelete && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                                    onClick={handleDelete}
                                    disabled={saving || deleting}
                                >
                                    {deleting ? t('app.addTransaction.form.deleting') : t('app.addTransaction.form.delete')}
                                </Button>
                            )}

                            {!readOnly && (
                            <HubDialogSubmitButton
                                type="submit"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                disabled={saving || deleting || submitDisabled}
                            >
                                {saving
                                    ? (submittingLabel || t('app.addTransaction.form.updating'))
                                    : (submitLabel || t('app.addTransaction.form.updateTransaction'))}
                            </HubDialogSubmitButton>
                            )}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </form>
            </Box>
        </Box>
    );
};

export default TransactionForm;
